import React, { useState, useEffect } from 'react'
import {
  Clock,
  DollarSign,
  Zap,
  CheckCircle,
  AlertCircle,
  Info,
  TrendingUp,
  Shield,
  Globe,
  Banknote
} from 'lucide-react'

interface SettlementRailOption {
  rail: 'FPS' | 'CIPS' | 'e-CNY'
  name: string
  description: string
  estimatedTime: string
  estimatedTimeMinutes: number
  cost: number
  costPercentage: number
  availability: 'available' | 'limited' | 'unavailable'
  features: string[]
  icon: React.ReactNode
  color: string
  bgColor: string
  borderColor: string
  recommended?: boolean
  recommendationReason?: string
}

interface SettlementRailSelectorProps {
  selectedRail: 'FPS' | 'CIPS' | 'e-CNY'
  onRailSelect: (rail: 'FPS' | 'CIPS' | 'e-CNY') => void
  amount: number
  currency: 'HKD' | 'CNY' | 'USD'
  stablecoin: 'USDT' | 'USDC' | 'HKD₮' | 'CNH₮'
  className?: string
  businessView?: boolean
  showConfiguration?: boolean
  onConfigurationChange?: (rail: 'FPS' | 'CIPS' | 'e-CNY', config: any) => void
}

export const SettlementRailSelector: React.FC<SettlementRailSelectorProps> = ({
  selectedRail,
  onRailSelect,
  amount,
  currency,
  stablecoin,
  className = '',
  businessView = false,
  showConfiguration = false,
  onConfigurationChange
}) => {
  const [railOptions, setRailOptions] = useState<SettlementRailOption[]>([])
  const [loading, setLoading] = useState(true)
  const [businessConfiguration, setBusinessConfiguration] = useState({
    FPS: {
      bankAccount: 'HSBC-HK-123456789',
      accountName: 'Hong Kong Electronics Ltd',
      autoSettle: true,
      settlementTime: 'immediate'
    },
    CIPS: {
      bankAccount: 'BOC-CN-987654321',
      accountName: 'Hong Kong Electronics Ltd',
      autoSettle: true,
      settlementTime: 'same-day',
      safeCode: 'AUTO-GENERATE'
    },
    'e-CNY': {
      walletId: 'eCNY-WALLET-456789',
      accountName: 'Hong Kong Electronics Ltd',
      autoSettle: true,
      settlementTime: 'instant'
    }
  })
  const [showConfigModal, setShowConfigModal] = useState<'FPS' | 'CIPS' | 'e-CNY' | null>(null)

  useEffect(() => {
    generateRailOptions()
  }, [amount, currency, stablecoin])

  const generateRailOptions = async () => {
    setLoading(true)
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const baseOptions: SettlementRailOption[] = [
      {
        rail: 'FPS',
        name: 'Faster Payment System',
        description: 'Instant HKD settlement via Hong Kong banks',
        estimatedTime: '15 minutes',
        estimatedTimeMinutes: 15,
        cost: amount * 0.001, // 0.1% fee
        costPercentage: 0.1,
        availability: currency === 'HKD' ? 'available' : 'limited',
        features: [
          'Real-time settlement',
          'Major HK banks supported',
          'Low processing fees',
          '24/7 availability'
        ],
        icon: <Zap className="w-5 h-5" />,
        color: '#10B981',
        bgColor: 'rgba(16, 185, 129, 0.1)',
        borderColor: '#10B981'
      },
      {
        rail: 'CIPS',
        name: 'Cross-border Interbank Payment System',
        description: 'Same-day CNH/CNY settlement via CIPS network',
        estimatedTime: 'Same day',
        estimatedTimeMinutes: 480, // 8 hours
        cost: amount * 0.002, // 0.2% fee
        costPercentage: 0.2,
        availability: ['CNY', 'HKD'].includes(currency) ? 'available' : 'limited',
        features: [
          'Direct CIPS integration',
          'BOC, ICBC, CCB supported',
          'SAFE compliance built-in',
          'Cross-border optimized'
        ],
        icon: <Globe className="w-5 h-5" />,
        color: '#3B82F6',
        bgColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: '#3B82F6'
      },
      {
        rail: 'e-CNY',
        name: 'Digital Yuan',
        description: 'Instant digital currency settlement',
        estimatedTime: 'Instant',
        estimatedTimeMinutes: 1,
        cost: amount * 0.0005, // 0.05% fee
        costPercentage: 0.05,
        availability: stablecoin === 'CNH₮' ? 'available' : 'limited',
        features: [
          'Instant settlement',
          'PBOC digital currency',
          'Lowest fees',
          'Future-ready technology'
        ],
        icon: <Banknote className="w-5 h-5" />,
        color: '#8B5CF6',
        bgColor: 'rgba(139, 92, 246, 0.1)',
        borderColor: '#8B5CF6'
      }
    ]

    // Add intelligent recommendations based on transaction parameters
    const optionsWithRecommendations = baseOptions.map(option => {
      let recommended = false
      let recommendationReason = ''

      // Recommendation logic
      if (option.rail === 'FPS' && currency === 'HKD' && amount < 50000) {
        recommended = true
        recommendationReason = 'Best for HKD payments under 50K - fastest settlement'
      } else if (option.rail === 'CIPS' && ['CNY', 'HKD'].includes(currency) && amount >= 50000) {
        recommended = true
        recommendationReason = 'Optimal for large cross-border payments'
      } else if (option.rail === 'e-CNY' && stablecoin === 'CNH₮' && amount < 100000) {
        recommended = true
        recommendationReason = 'Lowest cost for CNH₮ transactions'
      }

      return {
        ...option,
        recommended,
        recommendationReason
      }
    })

    setRailOptions(optionsWithRecommendations)
    setLoading(false)
  }

  const getAvailabilityStatus = (availability: string) => {
    switch (availability) {
      case 'available':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          text: 'Available',
          className: 'settlement-rail-selector__status--available'
        }
      case 'limited':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          text: 'Limited',
          className: 'settlement-rail-selector__status--limited'
        }
      case 'unavailable':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          text: 'Unavailable',
          className: 'settlement-rail-selector__status--unavailable'
        }
      default:
        return {
          icon: <Info className="w-4 h-4" />,
          text: 'Unknown',
          className: 'settlement-rail-selector__status--unknown'
        }
    }
  }

  if (loading) {
    return (
      <div className={`settlement-rail-selector ${className}`}>
        <div className="settlement-rail-selector__loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="settlement-rail-selector__loading-text">
            Analyzing settlement options...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={`settlement-rail-selector ${className}`}>
      <div className="settlement-rail-selector__header">
        <div className="settlement-rail-selector__title">
          <Shield className="w-5 h-5 text-blue-600" />
          <h3>Settlement Rail Selection</h3>
        </div>
        <div className="settlement-rail-selector__subtitle">
          Choose the optimal settlement path for your transaction
        </div>
      </div>

      <div className="settlement-rail-selector__options">
        {railOptions.map((option) => {
          const isSelected = selectedRail === option.rail
          const isAvailable = option.availability === 'available'
          const status = getAvailabilityStatus(option.availability)

          return (
            <button
              key={option.rail}
              onClick={() => isAvailable && onRailSelect(option.rail)}
              disabled={!isAvailable}
              className={`settlement-rail-selector__option ${
                isSelected ? 'settlement-rail-selector__option--selected' : ''
              } ${!isAvailable ? 'settlement-rail-selector__option--disabled' : ''}`}
              style={{
                borderColor: isSelected ? option.borderColor : undefined,
                backgroundColor: isSelected ? option.bgColor : undefined
              }}
            >
              {/* Recommendation Badge */}
              {option.recommended && (
                <div className="settlement-rail-selector__recommendation">
                  <TrendingUp className="w-4 h-4" />
                  <span>Recommended</span>
                </div>
              )}

              {/* Rail Header */}
              <div className="settlement-rail-selector__option-header">
                <div 
                  className="settlement-rail-selector__option-icon"
                  style={{ 
                    backgroundColor: option.bgColor,
                    color: option.color 
                  }}
                >
                  {option.icon}
                </div>
                <div className="settlement-rail-selector__option-title">
                  <div className="settlement-rail-selector__option-name">
                    {option.rail}
                  </div>
                  <div className="settlement-rail-selector__option-full-name">
                    {option.name}
                  </div>
                </div>
                <div className={`settlement-rail-selector__status ${status.className}`}>
                  {status.icon}
                  <span>{status.text}</span>
                </div>
              </div>

              {/* Rail Description */}
              <div className="settlement-rail-selector__option-description">
                {option.description}
              </div>

              {/* Time and Cost Estimates */}
              <div className="settlement-rail-selector__estimates">
                <div className="settlement-rail-selector__estimate">
                  <div className="settlement-rail-selector__estimate-icon">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div className="settlement-rail-selector__estimate-content">
                    <div className="settlement-rail-selector__estimate-label">
                      Settlement Time
                    </div>
                    <div className="settlement-rail-selector__estimate-value">
                      {option.estimatedTime}
                    </div>
                  </div>
                </div>

                <div className="settlement-rail-selector__estimate">
                  <div className="settlement-rail-selector__estimate-icon">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div className="settlement-rail-selector__estimate-content">
                    <div className="settlement-rail-selector__estimate-label">
                      Processing Fee
                    </div>
                    <div className="settlement-rail-selector__estimate-value">
                      {currency} {option.cost.toFixed(2)} ({option.costPercentage}%)
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="settlement-rail-selector__features">
                {option.features.map((feature, index) => (
                  <div key={index} className="settlement-rail-selector__feature">
                    <CheckCircle className="w-3 h-3" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Recommendation Reason */}
              {option.recommended && option.recommendationReason && (
                <div className="settlement-rail-selector__recommendation-reason">
                  <Info className="w-4 h-4" />
                  <span>{option.recommendationReason}</span>
                </div>
              )}

              {/* Selection Indicator */}
              {isSelected && (
                <div className="settlement-rail-selector__selected-indicator">
                  <CheckCircle className="w-5 h-5" />
                  <span>Selected</span>
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Business Configuration Section */}
      {businessView && showConfiguration && (
        <div className="settlement-rail-selector__business-config">
          <div className="settlement-rail-selector__config-header">
            <Shield className="w-4 h-4 text-green-600" />
            <span>Business Settlement Configuration</span>
          </div>
          <div className="settlement-rail-selector__config-content">
            {Object.entries(businessConfiguration).map(([rail, config]) => (
              <div key={rail} className="settlement-rail-selector__config-item">
                <div className="settlement-rail-selector__config-rail">
                  <div className="settlement-rail-selector__config-rail-header">
                    <span className="settlement-rail-selector__config-rail-name">{rail}</span>
                    <div className="settlement-rail-selector__config-status">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Configured</span>
                    </div>
                  </div>
                  <div className="settlement-rail-selector__config-details">
                    <div className="settlement-rail-selector__config-detail">
                      <span className="settlement-rail-selector__config-label">Account:</span>
                      <span className="settlement-rail-selector__config-value">
                        {rail === 'FPS' ? config.bankAccount : 
                         rail === 'CIPS' ? config.bankAccount : 
                         config.walletId}
                      </span>
                    </div>
                    <div className="settlement-rail-selector__config-detail">
                      <span className="settlement-rail-selector__config-label">Settlement:</span>
                      <span className="settlement-rail-selector__config-value">
                        {config.autoSettle ? `Auto (${config.settlementTime})` : 'Manual'}
                      </span>
                    </div>
                    {rail === 'CIPS' && (
                      <div className="settlement-rail-selector__config-detail">
                        <span className="settlement-rail-selector__config-label">SAFE Code:</span>
                        <span className="settlement-rail-selector__config-value">
                          {config.safeCode}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowConfigModal(rail as 'FPS' | 'CIPS' | 'e-CNY')}
                    className="settlement-rail-selector__config-edit"
                  >
                    Edit Configuration
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Information */}
      <div className="settlement-rail-selector__summary">
        <div className="settlement-rail-selector__summary-header">
          <Info className="w-4 h-4 text-blue-600" />
          <span>Settlement Rail Information</span>
        </div>
        <div className="settlement-rail-selector__summary-content">
          <div className="settlement-rail-selector__summary-item">
            <strong>FPS:</strong> Best for HKD payments with instant settlement through major Hong Kong banks
          </div>
          <div className="settlement-rail-selector__summary-item">
            <strong>CIPS:</strong> Optimal for cross-border CNY/CNH payments with direct mainland China access
          </div>
          <div className="settlement-rail-selector__summary-item">
            <strong>e-CNY:</strong> Future-ready digital currency with lowest fees and instant settlement
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="settlement-rail-selector__modal-overlay">
          <div className="settlement-rail-selector__modal">
            <div className="settlement-rail-selector__modal-header">
              <h3>Configure {showConfigModal} Settlement</h3>
              <button
                onClick={() => setShowConfigModal(null)}
                className="settlement-rail-selector__modal-close"
              >
                ×
              </button>
            </div>
            <div className="settlement-rail-selector__modal-content">
              <div className="settlement-rail-selector__modal-form">
                {showConfigModal === 'FPS' && (
                  <>
                    <div className="settlement-rail-selector__form-group">
                      <label>Bank Account</label>
                      <select 
                        value={businessConfiguration.FPS.bankAccount}
                        onChange={(e) => setBusinessConfiguration(prev => ({
                          ...prev,
                          FPS: { ...prev.FPS, bankAccount: e.target.value }
                        }))}
                      >
                        <option value="HSBC-HK-123456789">HSBC Hong Kong - ***6789</option>
                        <option value="SCB-HK-987654321">Standard Chartered - ***4321</option>
                        <option value="BOC-HK-456789123">Bank of China HK - ***9123</option>
                      </select>
                    </div>
                    <div className="settlement-rail-selector__form-group">
                      <label>Settlement Timing</label>
                      <select 
                        value={businessConfiguration.FPS.settlementTime}
                        onChange={(e) => setBusinessConfiguration(prev => ({
                          ...prev,
                          FPS: { ...prev.FPS, settlementTime: e.target.value }
                        }))}
                      >
                        <option value="immediate">Immediate (15 minutes)</option>
                        <option value="hourly">Hourly batches</option>
                        <option value="daily">Daily batches</option>
                      </select>
                    </div>
                  </>
                )}
                {showConfigModal === 'CIPS' && (
                  <>
                    <div className="settlement-rail-selector__form-group">
                      <label>CIPS Bank Account</label>
                      <select 
                        value={businessConfiguration.CIPS.bankAccount}
                        onChange={(e) => setBusinessConfiguration(prev => ({
                          ...prev,
                          CIPS: { ...prev.CIPS, bankAccount: e.target.value }
                        }))}
                      >
                        <option value="BOC-CN-987654321">Bank of China - ***4321</option>
                        <option value="ICBC-CN-123456789">ICBC - ***6789</option>
                        <option value="CCB-CN-456789123">CCB - ***9123</option>
                      </select>
                    </div>
                    <div className="settlement-rail-selector__form-group">
                      <label>SAFE Declaration</label>
                      <select 
                        value={businessConfiguration.CIPS.safeCode}
                        onChange={(e) => setBusinessConfiguration(prev => ({
                          ...prev,
                          CIPS: { ...prev.CIPS, safeCode: e.target.value }
                        }))}
                      >
                        <option value="AUTO-GENERATE">Auto-generate codes</option>
                        <option value="MANUAL">Manual declaration</option>
                      </select>
                    </div>
                  </>
                )}
                {showConfigModal === 'e-CNY' && (
                  <>
                    <div className="settlement-rail-selector__form-group">
                      <label>e-CNY Wallet</label>
                      <select 
                        value={businessConfiguration['e-CNY'].walletId}
                        onChange={(e) => setBusinessConfiguration(prev => ({
                          ...prev,
                          'e-CNY': { ...prev['e-CNY'], walletId: e.target.value }
                        }))}
                      >
                        <option value="eCNY-WALLET-456789">Primary e-CNY Wallet - ***6789</option>
                        <option value="eCNY-WALLET-123456">Secondary e-CNY Wallet - ***3456</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="settlement-rail-selector__form-group">
                  <label className="settlement-rail-selector__checkbox-label">
                    <input
                      type="checkbox"
                      checked={businessConfiguration[showConfigModal].autoSettle}
                      onChange={(e) => setBusinessConfiguration(prev => ({
                        ...prev,
                        [showConfigModal]: { 
                          ...prev[showConfigModal], 
                          autoSettle: e.target.checked 
                        }
                      }))}
                    />
                    Enable automatic settlement
                  </label>
                </div>
              </div>
              <div className="settlement-rail-selector__modal-actions">
                <button
                  onClick={() => setShowConfigModal(null)}
                  className="settlement-rail-selector__btn settlement-rail-selector__btn--secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onConfigurationChange?.(showConfigModal, businessConfiguration[showConfigModal])
                    setShowConfigModal(null)
                  }}
                  className="settlement-rail-selector__btn settlement-rail-selector__btn--primary"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}